import {
  Box,
  Center,
  Group,
  LoadingOverlay,
  Pagination,
  SegmentedControl,
  Stack,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useLocation } from 'wouter';
import { useMemo } from 'react';
import {
  getStatusCounts,
  useDownloadInvoicePDF,
  useInvoiceList,
  useInvoiceStatusCounts,
} from '../../models/invoices';
import {
  InvoiceSearchFilters,
  InvoiceSortField,
  InvoiceStatus,
  TInvoice,
} from '../../types';
import InvoiceSearchBar from '../InvoiceSearchBar';
import InvoiceTable from '../InvoiceTable';
import { getStatusIcon } from '../StatusBadge';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { cancelUrlDrafts, useUrlDraft, useUrlState } from '@/hooks/useUrlState';
import { compact, urlField } from '@/lib/urlState';
import { proformaStateLabel } from '@/lib/proformaState';

const INVOICE_STATUSES = Object.values(InvoiceStatus);
const STATE_TABS = [
  InvoiceStatus.ISSUED,
  InvoiceStatus.PAID,
  InvoiceStatus.CANCELLED,
] as const;
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const INVOICES_SCHEMA = {
  tab: urlField.enum<'all' | InvoiceStatus>(
    ['all', ...INVOICE_STATUSES],
    'all',
  ),
  page: urlField.page(),
  sortBy: urlField.enum<InvoiceSortField>(
    ['created_at', 'due_at', 'total_amount', 'status'],
    'created_at',
  ),
  sortOrder: urlField.enum(['asc', 'desc'] as const, 'desc'),
  source: urlField.enum(['all', 'offer', 'manual'] as const, 'all'),
  scope: urlField.enum(['live', 'test', 'all'] as const, 'live'),
  invoiceNumber: urlField.string(),
  clientName: urlField.string(),
  clientEmail: urlField.string(),
  status: urlField.enum<'' | InvoiceStatus>(['', ...INVOICE_STATUSES], ''),
  amount: urlField.number(null, { min: 0 }),
  currency: urlField.string(),
  dateFrom: urlField.string(),
  dateTo: urlField.string(),
  reported: urlField.boolean(),
  collection: urlField.enum<
    '' | NonNullable<InvoiceSearchFilters['collection']>
  >(['', 'none', 'partly', 'fully', 'overpaid'], ''),
};

const toDay = (value: string) => {
  if (!DAY_PATTERN.test(value)) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const fromDay = (date: Date | undefined) =>
  date && !Number.isNaN(date.getTime()) ? date.toISOString().split('T')[0] : '';

const toUrlPatch = (filters: InvoiceSearchFilters) => ({
  invoiceNumber: filters.invoiceNumber ?? '',
  clientName: filters.clientName ?? '',
  clientEmail: filters.clientEmail ?? '',
  status: (filters.status ?? '') as '' | InvoiceStatus,
  amount: filters.amount ?? null,
  currency: filters.currency ?? '',
  dateFrom: fromDay(filters.dateFrom),
  dateTo: fromDay(filters.dateTo),
  reported: !!filters.reported,
  collection: (filters.collection ?? '') as
    | ''
    | NonNullable<InvoiceSearchFilters['collection']>,
});

export default function InvoicesPage() {
  const [, navigate] = useLocation();

  const [state, setState, { clear, isFiltered }] = useUrlState(INVOICES_SCHEMA);
  const activeTab = state.tab;
  const currentPage = state.page;
  const sortBy = state.sortBy;
  const sortOrder = state.sortOrder;
  const sourceFilter = state.source;

  const searchFilters = useMemo(
    (): InvoiceSearchFilters =>
      compact({
        invoiceNumber: state.invoiceNumber || undefined,
        clientName: state.clientName || undefined,
        clientEmail: state.clientEmail || undefined,
        status: state.status || undefined,
        amount: state.amount ?? undefined,
        currency: state.currency || undefined,
        dateFrom: toDay(state.dateFrom),
        dateTo: toDay(state.dateTo),
        reported: state.reported || undefined,
        collection: state.collection || undefined,
      }),
    [state],
  );
  const [draftFilters, setDraftFilters] = useUrlDraft(searchFilters, (f) =>
    setState(toUrlPatch(f)),
  );

  const filtersWithSource = useMemo(
    () => ({ ...searchFilters, source: sourceFilter, scope: state.scope }),
    [searchFilters, sourceFilter, state.scope],
  );

  const { data: invoiceData, isLoading: isLoadingList } = useInvoiceList({
    page: currentPage,
    limit: 20,
    sortBy,
    sortOrder,
    status: activeTab as InvoiceStatus | 'all',
    filters: filtersWithSource,
    scope: state.scope,
  });

  const { data: statusCountsData } = useInvoiceStatusCounts(filtersWithSource);

  const downloadPDFMutation = useDownloadInvoicePDF();

  const invoices = useMemo(
    () => invoiceData?.invoices || [],
    [invoiceData?.invoices],
  );
  const statusCounts = useMemo(() => {
    if (statusCountsData) {
      // Use dedicated status counts endpoint data
      return {
        all: statusCountsData.all,
        [InvoiceStatus.ISSUED]: statusCountsData.issued,
        [InvoiceStatus.PAID]: statusCountsData.paid,
        [InvoiceStatus.CANCELLED]: statusCountsData.cancelled,
      };
    }

    // Fallback to backend counts from invoice list response
    if ((invoiceData as any)?.statusCounts) {
      const backendCounts = (invoiceData as any).statusCounts;
      return {
        all: parseInt(backendCounts.all || '0'),
        [InvoiceStatus.ISSUED]: parseInt(backendCounts.issued || '0'),
        [InvoiceStatus.PAID]: parseInt(backendCounts.paid || '0'),
        [InvoiceStatus.CANCELLED]: parseInt(backendCounts.cancelled || '0'),
      };
    }

    // Final fallback to local calculation
    return getStatusCounts(invoices);
  }, [statusCountsData, invoiceData, invoices]);

  // Event handlers
  const handleSort = (field: string) => {
    // Only allow valid sort fields
    const validSortFields: InvoiceSortField[] = [
      'created_at',
      'due_at',
      'total_amount',
      'status',
    ];
    if (!validSortFields.includes(field as InvoiceSortField)) return;

    const sortField = field as InvoiceSortField;
    setState(
      {
        sortBy: sortField,
        sortOrder: sortBy === sortField && sortOrder === 'asc' ? 'desc' : 'asc',
      },
      { resetPage: false },
    );
  };

  const handleViewDetails = (invoice: TInvoice) => {
    // Open the shareable full-page invoice details.
    navigate(`~/invoices/${invoice.id}`);
  };

  const handleDownload = (invoice: TInvoice) => {
    downloadPDFMutation.mutate(invoice.id.toString());
  };

  const handleSearch = () => {
    setState({ page: 1 });
  };

  const handleClearSearch = () => {
    cancelUrlDrafts();
    setState(toUrlPatch({}));
  };

  return (
    <Box p="md">
      <Stack gap="md">
        {/* Page Header */}
        <Group justify="space-between" align="center">
          <Title order={3}>Invoices</Title>
        </Group>

        {/* Source Filter */}
        <Group gap="xs">
          <SegmentedControl
            size="xs"
            value={sourceFilter}
            onChange={(val) => setState({ source: val as typeof state.source })}
            data={[
              { label: 'All', value: 'all' },
              { label: 'Offer', value: 'offer' },
              { label: 'Manual', value: 'manual' },
            ]}
          />
          <SegmentedControl
            size="xs"
            value={state.scope}
            onChange={(val) => setState({ scope: val as typeof state.scope })}
            data={[
              { label: 'Live', value: 'live' },
              {
                label: `Test${statusCountsData ? ` (${statusCountsData.test})` : ''}`,
                value: 'test',
              },
              { label: 'All', value: 'all' },
            ]}
          />
          <ClearFiltersButton visible={isFiltered} onClick={clear} />
        </Group>

        {/* Search Bar */}
        <InvoiceSearchBar
          filters={draftFilters}
          onFiltersChange={setDraftFilters}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          loading={isLoadingList}
        />

        {/* Status Tabs */}
        <Tabs
          value={activeTab}
          onChange={(value) =>
            setState({ tab: (value || 'all') as typeof state.tab })
          }
        >
          <Tabs.List>
            <Tabs.Tab
              value="all"
              leftSection={getStatusIcon(InvoiceStatus.ISSUED, 12)}
            >
              <Text fz="xs">All ({statusCounts.all})</Text>
            </Tabs.Tab>
            {STATE_TABS.map((s) => (
              <Tabs.Tab key={s} value={s} leftSection={getStatusIcon(s, 12)}>
                <Text fz="xs">
                  {proformaStateLabel(s)} ({statusCounts[s]})
                </Text>
              </Tabs.Tab>
            ))}
          </Tabs.List>
          {(['all', ...STATE_TABS] as const).map((tab) => (
            <Tabs.Panel key={tab} value={tab} pt={'md'}>
              <Box pos="relative">
                <LoadingOverlay visible={isLoadingList} />
                <Stack gap="md">
                  <InvoiceTable
                    invoices={invoices}
                    loading={isLoadingList}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSort={handleSort}
                    onViewDetails={handleViewDetails}
                    onDownloadPDF={handleDownload}
                    emptyMessage="No invoices found. Try adjusting your search or filter criteria."
                  />
                  {invoiceData &&
                    parseInt(String(invoiceData.total || '0')) > 0 && (
                      <Center>
                        <Pagination
                          value={currentPage}
                          onChange={(page) => setState({ page })}
                          total={(invoiceData as any).totalPages || 1}
                          size="sm"
                        />
                      </Center>
                    )}
                </Stack>
              </Box>
            </Tabs.Panel>
          ))}{' '}
        </Tabs>
      </Stack>
    </Box>
  );
}
